import { tLayout } from './layout-i18n';
import type { WorkStatusLogEntryDto } from '../api/attendance';

/**
 * 근무 상태내역 — 한 줄을 **무엇으로 그릴 것인가** (2026-09-06 · 판 C · 정본 P3-5).
 *
 * ## ★★★ 낱말은 새로 만들지 않는다
 * 상태 줄의 이름은 상태바 · 레일이 쓰는 것과 **같은 것**(`layout.workstatus.*`)이다.
 * 한 값이 자리마다 다른 이름을 가지면 같은 사람의 하루가 화면마다 달라 보인다 —
 * 판 A 가 레일에서 「출근」을 「근무중」으로 **고치지 않은** 것과 같은 규율이다.
 *
 * ## ★★ 배지는 「누가 남긴 것인가」다
 * 실측으로 찍은 퇴근과 소정 종료로 **마감한** 퇴근을 같은 배지로 그리면, 나중에
 * 「이 18:30 은 실제로 나간 시각인가」를 물을 수 없다. 퇴근 취소는 **사실을 지운** 일이라
 * 같은 「본인」이라도 톤이 다르다.
 *
 * ## ★ 시각 오름차순 — 서버가 이미 그렇게 준다
 * 옆의 24시간 띠가 왼쪽부터 이른 시각이라 목록도 같은 방향으로 읽혀야 한다.
 * (정본 E9 「Timeline」은 «최신이 위» 지만, 그것은 **띠 없이 홀로 서는 이력**의 규칙이다.)
 */

export type WorkLogTone = 'ok' | 'mut' | 'warn';

export interface WorkLogLine {
  /** 벽시계 `HH:MM`. */
  at: string;
  /** 「출근」 · 「퇴근」 · 「퇴근 취소」 · 「회의중」… */
  label: string;
  /** 「서버 기록」 · 「본인」 · 「소정 종료 마감」. */
  badge: string;
  tone: WorkLogTone;
  /** 곁말 — 지금은 「지각 N분」 하나뿐이다. 없으면 그리지 않는다. */
  note?: string;
}

/** `HH:MM` → 분. 못 읽으면 null(0 으로 지어내면 자정에 있었던 일이 된다). */
export function hhmmToMinutes(v: string | null | undefined): number | null {
  if (!v) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 47 || min > 59) return null;
  return h * 60 + min;
}

/**
 * 몇 분 늦었나 — **기준 시각을 모르면 «모른다»**(null). 0 으로 지어내면
 * 「정시에 왔다」가 된다.
 */
export function lateMinutes(at: string, baseStart: string | null | undefined): number | null {
  const a = hhmmToMinutes(at);
  const b = hhmmToMinutes(baseStart);
  if (a === null || b === null) return null;
  return a > b ? a - b : null;
}

const SOURCE_KEY = {
  RECORD: 'layout.worklog.src.record',
  SELF: 'layout.worklog.src.self',
  CLOSED: 'layout.worklog.src.closed',
} as const;

/**
 * 이력 한 줄 → 화면 한 줄.
 *
 * @param opts.baseStart 그날 기준 출근 시각 `HH:MM` — 「지각 N분」을 재는 자.
 * @param opts.late      서버가 판정한 지각 여부. **이 값이 참일 때만** 곁말을 단다 —
 *                       화면이 제 손으로 지각을 판정하면 서버 판정과 갈린다.
 */
export function toWorkLogLine(
  entry: WorkStatusLogEntryDto,
  opts: { baseStart?: string | null; late?: boolean } = {},
): WorkLogLine {
  const badge = tLayout(SOURCE_KEY[entry.source] ?? SOURCE_KEY.SELF);
  if (entry.kind === 'CHECK_IN') {
    const mins = opts.late ? lateMinutes(entry.at, opts.baseStart) : null;
    return {
      at: entry.at,
      label: tLayout('layout.worklog.check_in'),
      badge,
      tone: entry.source === 'SELF' ? 'mut' : 'ok',
      ...(mins !== null ? { note: tLayout('layout.worklog.late', { n: mins }) } : {}),
    };
  }
  if (entry.kind === 'CHECK_OUT') {
    return {
      at: entry.at,
      label: tLayout('layout.worklog.check_out'),
      badge,
      // 마감은 실측이 아니다 — 「서버 기록」과 같은 톤으로 그리면 그 차이가 사라진다.
      tone: entry.source === 'CLOSED' ? 'mut' : 'ok',
    };
  }
  if (entry.kind === 'CHECK_OUT_CANCEL') {
    return { at: entry.at, label: tLayout('layout.worklog.check_out_cancel'), badge, tone: 'warn' };
  }
  return {
    at: entry.at,
    // 상태 낱말은 상태바·레일과 **같은 것**을 쓴다.
    label: entry.status ? tLayout(`layout.workstatus.${entry.status}`) : tLayout('layout.workstatus.none'),
    badge,
    tone: 'mut',
  };
}

/** 목록 전체. 서버가 이미 시각 오름차순으로 준다 — 여기서 다시 세우지 않는다. */
export function toWorkLogLines(
  entries: readonly WorkStatusLogEntryDto[],
  opts: { baseStart?: string | null; late?: boolean } = {},
): WorkLogLine[] {
  return entries.map((e) => toWorkLogLine(e, opts));
}

/**
 * **옛 서버는 이력을 안 준다** — 그때는 사실 기록에서 두 줄을 만든다.
 *
 * ★★ 서버도 같은 일을 한다(이력이 없는 날). 여기 있는 것은 **프론트가 먼저 올라갔을 때**
 *    화면이 종전(출근·퇴근 두 줄)과 똑같이 도는 것을 지키기 위해서다 — 없으면 「그날
 *    아무 일도 없었다」로 읽힌다.
 * ★ 지어낸 줄이 아니다: 두 값 다 **사실 기록**이라 배지는 「서버 기록」이 맞다.
 */
export function fallbackLogFrom(day: {
  checkIn?: string | null; checkOut?: string | null;
}): WorkStatusLogEntryDto[] {
  const out: WorkStatusLogEntryDto[] = [];
  if (day.checkIn) out.push({ at: day.checkIn, kind: 'CHECK_IN', source: 'RECORD' });
  if (day.checkOut) out.push({ at: day.checkOut, kind: 'CHECK_OUT', source: 'RECORD' });
  return out;
}
